/**
 * API Configuration Utility
 *
 * This utility provides centralized configuration for API URLs
 * and helps ensure consistent handling across the application.
 */

// Determine if we're running locally or in production
const isLocal = () =>
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

// Production API URL - centralized for easy updates
// Use the dedicated worker URL for API requests
const PRODUCTION_API_URL = `${window.location.protocol}//${window.location.host}`; // Changed from 'https://virtualpat-demo.rdavido.workers.dev'

/**
 * Get the base API URL based on the current environment
 * @returns {string} The base API URL
 */
export const getBaseApiUrl = () => {
  // In development, make requests to the same origin (port 3000)
  // Vite will proxy /api requests to the backend on port 4000
  return isLocal() ? '' : PRODUCTION_API_URL;
};

/**
 * Get the full API URL for a specific endpoint
 * @param {string} endpoint - The API endpoint (should start with '/')
 * @returns {string} The full API URL
 */
export const getApiUrl = endpoint => {
  const baseUrl = getBaseApiUrl();

  // Handle undefined or null endpoint
  if (!endpoint) {
    console.warn('[ApiConfig] getApiUrl called with undefined/null endpoint');
    return baseUrl;
  }

  if (/^https?:\/\//i.test(endpoint)) {
    return endpoint;
  }

  // Ensure endpoint starts with a slash
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${baseUrl}${normalizedEndpoint}`;
};

/**
 * Get appropriate fetch options based on the environment
 * @param {Object} options - Additional fetch options
 * @param {boolean} includeAuth - Whether to include auth token in headers
 * @returns {Object} Fetch options with appropriate credentials and headers
 */
export const getFetchOptions = (options = {}, includeAuth = true) => {
  // Try to get token from localStorage (for cross-tab persistence)
  const legacyToken = localStorage.getItem('token');
  const accessToken = localStorage.getItem('_at');
  const token = accessToken || legacyToken;

  // Start with basic options
  const defaultOptions = {
    method: options.method || 'GET',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(includeAuth && token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
    // Always include credentials for cookie-based authentication
    credentials: 'include',
  };

  // Add body if provided (and not GET method)
  if (options.body && defaultOptions.method !== 'GET') {
    defaultOptions.body = options.body;
  }

  // Add any other options
  Object.keys(options).forEach(key => {
    if (!['headers', 'body', 'method'].includes(key)) {
      defaultOptions[key] = options[key];
    }
  });

  return defaultOptions;
};

export default {
  isLocal,
  PRODUCTION_API_URL,
  getBaseApiUrl,
  getApiUrl,
  getFetchOptions,
};
